/**
 * Create-ticket dialog.
 *
 * Deliberately short: title plus the handful of fields a tech actually knows at intake. Anything
 * else is faster to set on the detail page once the ticket exists, and a long modal is the main
 * reason people stop logging small jobs.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import toast from 'react-hot-toast';

import { Button } from '@/components/common/Button';
import { MarkdownEditor } from '@/components/common/MarkdownEditor';
import { CompanyPicker } from '@/components/tickets/CompanyPicker';
import { CustomFieldInputs } from '@/components/tickets/CustomFieldInputs';
import { useCreateTicket, useCustomFields, useTaxonomy } from '@/hooks/useServiceDesk';
import { extractDetail } from '@/services/api';
import { Select } from '@/components/common/Select';

export function NewTicketDialog({
  open,
  onClose,
  parentId,
}: {
  open: boolean;
  onClose: () => void;
  /** Set when creating a sub-ticket from a ticket's detail page. */
  parentId?: string;
}) {
  const navigate = useNavigate();
  const { data: taxonomy } = useTaxonomy();
  const create = useCreateTicket();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [contactId, setContactId] = useState<string | null>(null);
  const [queueId, setQueueId] = useState('');
  const [priorityId, setPriorityId] = useState('');
  const [tags, setTags] = useState('');
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const { data: fieldDefinitions } = useCustomFields('ticket');

  // Reset between openings so a cancelled draft doesn't reappear in the next ticket.
  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setCompanyId(null);
    setContactId(null);
    setQueueId('');
    setPriorityId('');
    setTags('');
    setCustomFields({});
  }, [open]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) return;
    create.mutate(
      {
        title: title.trim(),
        description: description.trim() || null,
        company_id: companyId,
        contact_id: contactId,
        queue_id: queueId || null,
        priority_id: priorityId || null,
        parent_id: parentId ?? null,
        custom_fields: customFields,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      },
      {
        onSuccess: (ticket) => {
          toast.success(`Created ${ticket.number}`);
          onClose();
          // Sub-tickets keep you on the parent — you're usually adding several in a row.
          if (!parentId) navigate(`/tickets/${ticket.id}`);
        },
        onError: (error) => toast.error(extractDetail(error)),
      },
    );
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-surface-900/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
          <Transition.Child
            as={Fragment}
            enter="transition duration-200 ease-out"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition duration-150 ease-in"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-xl rounded-xl bg-white shadow-modal dark:bg-surface-800">
              <form onSubmit={submit}>
                <div className="border-b border-surface-200 px-6 py-4 dark:border-surface-700">
                  <Dialog.Title className="text-lg font-semibold text-surface-900 dark:text-surface-100">
                    {parentId ? 'New sub-ticket' : 'New ticket'}
                  </Dialog.Title>
                </div>

                <div className="space-y-4 px-6 py-5">
                  <Field label="Title" required>
                    <input
                      autoFocus
                      required
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="input-base"
                      placeholder="Laptop won't boot after update"
                    />
                  </Field>

                  <Field label="Description">
                    <MarkdownEditor
                      value={description}
                      onChange={setDescription}
                      rows={4}
                      ariaLabel="Description"
                      placeholder="Markdown supported"
                    />
                  </Field>

                  <CompanyPicker
                    companyId={companyId}
                    contactId={contactId}
                    onCompanyChange={(id) => {
                      setCompanyId(id);
                      // The old contact belongs to the old company; keeping it would attach a
                      // ticket to a person at a different client.
                      setContactId(null);
                    }}
                    onContactChange={setContactId}
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Queue">
                      <Select
                        ariaLabel="Queue"
                        value={queueId}
                        onChange={setQueueId}
                        options={[
                          { value: '', label: 'Default' },
                          ...(taxonomy?.queues ?? []).map((q) => ({ value: q.id, label: q.name })),
                        ]}
                      />
                    </Field>
                    <Field label="Priority">
                      <Select
                        ariaLabel="Priority"
                        value={priorityId}
                        onChange={setPriorityId}
                        options={[
                          { value: '', label: 'Default' },
                          ...(taxonomy?.priorities ?? []).map((entry) => ({
                            value: entry.id,
                            label: entry.name,
                          })),
                        ]}
                      />
                    </Field>
                  </div>

                  {/*
                    Admin-defined fields, scoped to the chosen queue. A field created in admin
                    shows up here with no frontend change — the Phase 1 exit criterion.
                  */}
                  <CustomFieldInputs
                    definitions={fieldDefinitions ?? []}
                    values={customFields}
                    onChange={setCustomFields}
                    queueId={queueId || null}
                  />

                  <Field label="Tags" hint="Comma separated. New tags are created as you type them.">
                    <input
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      className="input-base"
                      placeholder="hardware, onsite"
                    />
                  </Field>
                </div>

                <div className="flex justify-end gap-2 border-t border-surface-200 px-6 py-4 dark:border-surface-700">
                  <Button type="button" variant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={create.isPending} disabled={!title.trim()}>
                    Create ticket
                  </Button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

function Field({
  label,
  children,
  required,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-200">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-surface-500">{hint}</span>}
    </label>
  );
}
